import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Form, 
  Input, 
  Select, 
  Pagination, 
  Message, 
  Header, 
  Icon, 
  Label, 
  Modal, 
  Grid,
  Segment,
  Divider,
  Tab,
  Container
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import useAppList from '../../helpers/useAppList';


const typeOptions = [
  { key: 'all', text: '全部类型', value: '' },
  { key: 'automation', text: '自动化脚本', value: 1 },
  { key: 'ai', text: 'AI相关脚本', value: 2 },
  { key: 'dataproc', text: '数据处理', value: 3 },
  { key: 'webscrape', text: '网页抓取', value: 4 },
  { key: 'systemops', text: '系统操作', value: 5 },
  { key: 'utility', text: '实用工具', value: 6 }
];

const difficultyOptions = [
  { key: 'all', text: '全部难度', value: '' },
  { key: 'beginner', text: '初级', value: 1 },
  { key: 'intermediate', text: '中级', value: 2 },
  { key: 'advanced', text: '高级', value: 3 },
  { key: 'expert', text: '专家', value: 4 }
];

const languageOptions = [
  { key: 'all', text: '全部语言', value: '' },
  { key: 'python', text: 'Python', value: 'python' },
  { key: 'javascript', text: 'JavaScript', value: 'javascript' },
  { key: 'bash', text: 'Bash', value: 'bash' },
  { key: 'powershell', text: 'PowerShell', value: 'powershell' },
  { key: 'java', text: 'Java', value: 'java' },
  { key: 'csharp', text: 'C#', value: 'csharp' }
];

const ExampleLibrary = () => {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [pageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [selectedExample, setSelectedExample] = useState(null);
  const [showExampleModal, setShowExampleModal] = useState(false);

  // 使用App列表hook
  const { getAppNames } = useAppList();

  const loadExamples = async () => {
    setLoading(true);
    try {
      let url = `/api/examples/published?page=${activePage}&page_size=${pageSize}`;
      if (typeFilter) url += `&type=${typeFilter}`;
      if (difficultyFilter) url += `&difficulty=${difficultyFilter}`;
      if (languageFilter) url += `&language=${languageFilter}`;
      if (keyword) url += `&keyword=${encodeURIComponent(keyword)}`;
      
      const res = await API.get(url);
      const { success, message, data } = res.data;
      if (success) {
        const examples = data.examples || [];
        const total = data.total || 0;
        setExamples(examples);
        setTotal(total);
      } else {
        showError(message || '获取脚本列表失败');
        setExamples([]);
        setTotal(0);
      }
    } catch (error) {
      showError('获取脚本列表失败: ' + error.message);
      setExamples([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setActivePage(1);
    await loadExamples();
    setSearchLoading(false);
  };

  const handlePageChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  const showExampleDetails = async (exampleId) => {
    try {
      const res = await API.get(`/api/examples/${exampleId}`);
      const { success, message, data } = res.data;
      if (success) {
        setSelectedExample(data);
        setShowExampleModal(true);
      } else {
        showError(message || '加载脚本案例详情失败');
      }
    } catch (error) {
      showError('加载脚本案例详情失败: ' + error.message);
    }
  };

  const handleInteraction = async (exampleId, action) => {
    try {
      const res = await API.post(`/api/examples/${exampleId}/interact`, { action });
      const { success, message } = res.data;
      if (success) {
        showSuccess(message);
        // 重新加载列表以更新统计数据
        loadExamples();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('操作失败');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showSuccess('代码已复制到剪贴板');
    }).catch(() => {
      showError('复制失败');
    });
  };

  useEffect(() => {
    loadExamples();
  }, [activePage, typeFilter, difficultyFilter, languageFilter]);

  const getTypeColor = (type) => {
    switch (type) {
      case 1: return 'blue';    // 自动化脚本
      case 2: return 'purple';  // AI相关脚本
      case 3: return 'green';   // 数据处理
      case 4: return 'orange';  // 网页抓取
      case 5: return 'red';     // 系统操作
      case 6: return 'teal';    // 实用工具
      default: return 'grey';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 1: return '自动化脚本';
      case 2: return 'AI相关脚本';
      case 3: return '数据处理';
      case 4: return '网页抓取';
      case 5: return '系统操作';
      case 6: return '实用工具';
      default: return '未知';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 1: return 'green';   // 初级
      case 2: return 'yellow';  // 中级
      case 3: return 'orange';  // 高级
      case 4: return 'red';     // 专家
      default: return 'grey';
    }
  };

  const getDifficultyText = (difficulty) => {
    switch (difficulty) {
      case 1: return '初级';
      case 2: return '中级';
      case 3: return '高级';
      case 4: return '专家';
      default: return '未知';
    }
  };

  if (loading && examples.length === 0) {
    return (
      <Container>
        <Segment loading style={{ minHeight: '200px' }}>
          <Header as="h2">
            <Icon name="book" />
            <Header.Content>
              脚本案例库
              <Header.Subheader>发现和学习优秀的脚本案例</Header.Subheader>
            </Header.Content>
          </Header>
        </Segment>
      </Container>
    );
  }

  return (
    <Container>
      <Header as="h2">
        <Icon name="book" />
        <Header.Content>
          脚本案例库
          <Header.Subheader>发现和学习优秀的脚本案例</Header.Subheader>
        </Header.Content>
      </Header>

      {/* 搜索和筛选 */}
      <Segment>
        <Form>
          <Form.Group widths='equal'>
            <Form.Field>
              <Input
                icon="search"
                placeholder="搜索案例标题、描述..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Form.Field>
            <Form.Field>
              <Select
                placeholder="选择类型"
                options={typeOptions}
                value={typeFilter}
                onChange={(e, { value }) => setTypeFilter(value)}
              />
            </Form.Field>
            <Form.Field>
              <Select
                placeholder="选择难度"
                options={difficultyOptions}
                value={difficultyFilter}
                onChange={(e, { value }) => setDifficultyFilter(value)}
              />
            </Form.Field>
            <Form.Field>
              <Select
                placeholder="选择语言"
                options={languageOptions}
                value={languageFilter}
                onChange={(e, { value }) => setLanguageFilter(value)}
              />
            </Form.Field>
          </Form.Group>
          
          <Form.Group>
            <Form.Field>
              <Button 
                primary 
                loading={searchLoading}
                onClick={handleSearch}
                icon="search"
                content="搜索"
              />
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      {/* 脚本案例卡片列表 */}
      <Card.Group itemsPerRow={3} stackable>
        {examples.map((example) => (
          <Card key={example.id}>
            <Card.Content>
              <Card.Header style={{ fontSize: '16px' }}>
                {example.title}
              </Card.Header>
              <Card.Meta>
                <div style={{ marginBottom: '8px' }}>
                  <Label color={getTypeColor(example.type)} size="small">
                    {getTypeText(example.type)}
                  </Label>
                  <Label color={getDifficultyColor(example.difficulty)} size="small">
                    {getDifficultyText(example.difficulty)}
                  </Label>
                  <Label color="blue" size="small">
                    <Icon name="code" />
                    {example.language}
                  </Label>
                </div>
              </Card.Meta>
              <Card.Description style={{ minHeight: '60px' }}>
                {example.description}
              </Card.Description>
              
              {/* 关联App */}
              <div style={{ marginTop: '10px' }}>
                {(() => {
                  try {
                    const relatedApps = JSON.parse(example.related_apps || '[]');
                    if (relatedApps.length > 0) {
                      return (
                        <div>
                          <small style={{ color: '#666' }}>关联App: </small>
                          {getAppNames(relatedApps).map((appName, index) => (
                            <Label key={index} size="mini" color="teal" style={{ marginRight: '3px' }}>
                              <Icon name="mobile alternate" />
                              {appName}
                            </Label>
                          ))}
                        </div>
                      );
                    }
                  } catch (e) {}
                  return null;
                })()}
              </div>

              {/* 标签 */}
              <div style={{ marginTop: '8px' }}>
                {(() => {
                  try {
                    const tags = JSON.parse(example.tags || '[]');
                    return tags.slice(0, 3).map((tag, index) => (
                      <Label key={index} size="mini" style={{ marginRight: '3px', marginBottom: '3px' }}>
                        {tag}
                      </Label>
                    ));
                  } catch (e) {
                    return null;
                  }
                })()}
              </div>
            </Card.Content>
            <Card.Content extra>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <Icon name="eye" />{example.view_count}
                  <Icon name="heart" style={{ marginLeft: '10px' }} />{example.like_count}
                  <Icon name="download" style={{ marginLeft: '10px' }} />{example.download_count}
                </div>
                <Button.Group size="mini">
                  <Button 
                    primary
                    onClick={() => showExampleDetails(example.id)}
                  >
                    查看详情
                  </Button>
                  <Button 
                    color="red"
                    icon="heart"
                    onClick={() => handleInteraction(example.id, 2)} // 点赞
                  />
                  <Button 
                    color="green"
                    icon="download"
                    onClick={() => handleInteraction(example.id, 3)} // 下载
                  />
                </Button.Group>
              </div>
            </Card.Content>
          </Card>
        ))}
      </Card.Group>

      {/* 分页 */}
      <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '30px' }}>
        <Pagination
          activePage={activePage}
          onPageChange={handlePageChange}
          totalPages={Math.ceil(total / pageSize)}
          ellipsisItem={{ content: <Icon name='ellipsis horizontal' />, icon: true }}
          firstItem={{ content: <Icon name='angle double left' />, icon: true }}
          lastItem={{ content: <Icon name='angle double right' />, icon: true }}
          prevItem={{ content: <Icon name='angle left' />, icon: true }}
          nextItem={{ content: <Icon name='angle right' />, icon: true }}
        />
        <div style={{ marginTop: '10px', color: '#666' }}>
          共 {total} 个案例，第 {activePage} 页
        </div>
      </div>

      {/* 脚本案例详情模态框 */}
      <Modal open={showExampleModal} onClose={() => setShowExampleModal(false)} size="large">
        <Modal.Header>
          <Icon name="code" />
          {selectedExample?.title}
        </Modal.Header>
        <Modal.Content>
          {selectedExample && (
            <Tab panes={[
              {
                menuItem: '案例概览',
                render: () => (
                  <Tab.Pane>
                    <Grid stackable>
                      <Grid.Row>
                        <Grid.Column width={10}>
                          <Segment>
                            <Header as="h4">案例描述</Header>
                            <p>{selectedExample.description}</p>
                            
                            {selectedExample.natural_language && (
                              <>
                                <Header as="h4">功能说明</Header>
                                <p style={{ 
                                  background: '#f9f9f9', 
                                  padding: '10px', 
                                  borderRadius: '4px',
                                  borderLeft: '4px solid #2185d0'
                                }}>
                                  {selectedExample.natural_language}
                                </p>
                              </>
                            )}
                          </Segment>
                        </Grid.Column>
                        <Grid.Column width={6}>
                          <Segment>
                            <Header as="h4">案例信息</Header>
                            <p><strong>类型:</strong> 
                              <Label color={getTypeColor(selectedExample.type)} style={{ marginLeft: '10px' }}>
                                {getTypeText(selectedExample.type)}
                              </Label>
                            </p>
                            <p><strong>难度:</strong> 
                              <Label color={getDifficultyColor(selectedExample.difficulty)} style={{ marginLeft: '10px' }}>
                                {getDifficultyText(selectedExample.difficulty)}
                              </Label>
                            </p>
                            <p><strong>编程语言:</strong> 
                              <Label color="blue" style={{ marginLeft: '10px' }}>
                                {selectedExample.language}
                              </Label>
                            </p>
                            <p><strong>浏览次数:</strong> {selectedExample.view_count}</p>
                            <p><strong>点赞次数:</strong> {selectedExample.like_count}</p>
                            <p><strong>下载次数:</strong> {selectedExample.download_count}</p>
                          </Segment>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Tab.Pane>
                )
              },
              {
                menuItem: '代码内容',
                render: () => (
                  <Tab.Pane>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <Header as="h4">代码</Header>
                      <Button 
                        size="small"
                        color="blue"
                        icon="copy"
                        content="复制代码"
                        onClick={() => copyToClipboard(selectedExample.code)}
                      />
                    </div>
                    <pre style={{ 
                      background: '#f4f4f4', 
                      padding: '15px', 
                      borderRadius: '4px',
                      display: 'block',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                      fontSize: '13px',
                      lineHeight: '1.4',
                      overflow: 'auto',
                      maxHeight: '500px',
                      border: '1px solid #ddd'
                    }}>
                      {selectedExample.code}
                    </pre>
                  </Tab.Pane>
                )
              },
              {
                menuItem: '使用说明',
                render: () => (
                  <Tab.Pane>
                    <Grid stackable>
                      <Grid.Row>
                        <Grid.Column width={16}>
                          {selectedExample.prerequisites && (
                            <Segment>
                              <Header as="h4">
                                <Icon name="exclamation triangle" color="orange" />
                                前置条件
                              </Header>
                              <p>{selectedExample.prerequisites}</p>
                            </Segment>
                          )}
                          
                          {selectedExample.expected_output && (
                            <Segment>
                              <Header as="h4">
                                <Icon name="checkmark" color="green" />
                                预期输出
                              </Header>
                              <p>{selectedExample.expected_output}</p>
                            </Segment>
                          )}
                          
                          {selectedExample.notes && (
                            <Segment>
                              <Header as="h4">
                                <Icon name="info circle" color="blue" />
                                注意事项
                              </Header>
                              <p>{selectedExample.notes}</p>
                            </Segment>
                          )}
                          
                          {selectedExample.suitable_devices && (
                            <Segment>
                              <Header as="h4">适用设备</Header>
                              {(() => {
                                try {
                                  const devices = JSON.parse(selectedExample.suitable_devices);
                                  return devices.map((device, index) => (
                                    <Label key={index} size="small" style={{ marginRight: '5px', marginBottom: '5px' }}>
                                      <Icon name="computer" />
                                      {device}
                                    </Label>
                                  ));
                                } catch (e) {
                                  return <p>设备信息格式错误</p>;
                                }
                              })()}
                            </Segment>
                          )}
                          
                          {selectedExample.tags && (
                            <Segment>
                              <Header as="h4">标签</Header>
                              {(() => {
                                try {
                                  const tags = JSON.parse(selectedExample.tags);
                                  return tags.map((tag, index) => (
                                    <Label key={index} size="small" style={{ marginRight: '5px', marginBottom: '5px' }}>
                                      {tag}
                                    </Label>
                                  ));
                                } catch (e) {
                                  return <p>标签格式错误</p>;
                                }
                              })()}
                            </Segment>
                          )}
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Tab.Pane>
                )
              }
            ]} />
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button 
            color="red"
            icon="heart"
            content="点赞"
            onClick={() => {
              handleInteraction(selectedExample.id, 2);
              setShowExampleModal(false);
            }}
          />
          <Button 
            color="green"
            icon="download"
            content="下载"
            onClick={() => {
              handleInteraction(selectedExample.id, 3);
              copyToClipboard(selectedExample.code);
              setShowExampleModal(false);
            }}
          />
          <Button onClick={() => setShowExampleModal(false)}>
            <Icon name="close" />
            关闭
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default ExampleLibrary;
